import { createBrowserRouter, Navigate } from "react-router-dom";
import App from "@/App";
import DashboardLayout from "./components/layouts/DashboardLayout";
import Page404 from "./pages/Page404";
import AccessDenied from "./pages/AccessDenied";
import Login from "./pages/Login";
import RootLayout from "./components/layouts/RootLayout";
import Dashboard from "./pages/Dashboard";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import ChangePassword from "./pages/ChangePassword";
import Braches from "./pages/Branches";
import SavingSeasons from "./pages/SavingSeasons";
import Members from "./pages/Members";
import Groups from "./pages/Groups";
import Contributions from "./pages/Contributions";
import Savings from "./pages/Savings";
import Loans from "./pages/Loans";
import LoanRepayments from "./pages/LoanRepayments";
import Fines from "./pages/Fines";
import ExpenseCategories from "./pages/ExpenseCategories";
import RolesPermissions from "./pages/RolesPermissions";
import ApprovalWorkflowsSettings from "./pages/ApprovalWorkflowsSettings";
import SystemUsers from "./pages/SystemUsers";
import AccountSettings from "./pages/AccountSettings";
import GeneralSettings from "./pages/GeneralSettings";
import Settings from "./pages/Settings";
import ErrorBoundary from "./components/ErrorBoundary";
import UsersRoles from "./pages/UsersRoles";
import LoanProductForm from "./pages/LoanProductForm";
import Expenses from "./pages/Expenses";
import Attendance from "./pages/Attendance";
import EnterpriseAttendance from "./pages/EnterpriseAttendance";
import Meetings from "./pages/Meetings";
import MemberMeetings from "./pages/member/MemberMeetings";
import Districts from "./pages/Districts";
import Statements from "./pages/Statements";
import ApprovalsCenter from "./pages/ApprovalsCenter";
import InterestDistribution from "./pages/InterestDistribution";
import FinanceAccounts from "./pages/finance/Accounts";
import AccountDetails from "./pages/finance/AccountDetails";
import JournalEntries from "./pages/finance/JournalEntries";
import ChartOfAccounts from "./pages/finance/ChartOfAccounts";
import Ledger from "./pages/finance/Ledger";
import AuditLogs from "./pages/AuditLogs";
import FinancialReport from "./pages/reports/FinancialReport";
import MemberReport from "./pages/reports/MemberReport";
import MemberStatement from "./pages/reports/MemberStatement";
import SavingsReportPage from "./pages/reports/SavingsReport";
import LoanReportPage from "./pages/reports/LoanReport";
import RepaymentReportPage from "./pages/reports/RepaymentReport";
import PayrollReportPage from "./pages/reports/PayrollReport";
import ApprovalReportPage from "./pages/reports/ApprovalReport";
import MemberContributionStatement from "./pages/MemberContributionStatement";
import GroupInfo from "./pages/GroupInfo";
import HelpGuide from "./pages/HelpGuide";
import FAQ from "./pages/FAQ";
import ContactSupport from "./pages/ContactSupport";
import SystemStatus from "./pages/SystemStatus";

import HRPayroll from "./pages/HRPayroll";
import AIAssistant from "./pages/ai/AIAssistant";
import MemberSelfRegistration from "./pages/MemberSelfRegistration";
import MemberDashboard from "./pages/member/MemberDashboard";
import MyContributions from "./pages/member/MyContributions";
import RecordSaving from "./pages/member/RecordSaving";
import MyShares from "./pages/member/MyShares";
import MyPenalties from "./pages/member/MyPenalties";
import MyProfile from "./pages/member/MyProfile";
import MyLoans from "./pages/member/MyLoans";
import ApplyForLoan from "./pages/member/ApplyForLoan";
import MyGuarantorRequests from "./pages/member/MyGuarantorRequests";
import DeclareLoanPayment from "./pages/member/DeclareLoanPayment";
import PenaltyManagement from "./pages/admin/PenaltyManagement";
import DeclareMonthlyShares from "./pages/DeclareMonthlyShares";
import PendingMemberPayments from "./pages/admin/PendingMemberPayments";
import ReportsLayout from "./pages/member/reports/ReportsLayout";
import ReportsDashboard from "./pages/member/reports/ReportsDashboard";
import SavingsReport from "./pages/member/reports/SavingsReport";
import ContributionsReport from "./pages/member/reports/ContributionsReport";
import LoanReport from "./pages/member/reports/LoanReport";
import RepaymentsReport from "./pages/member/reports/RepaymentsReport";
import AccountStatement from "./pages/member/reports/AccountStatement";
import SelectInsurers from "./pages/member/SelectInsurers";
import MemberSettings from "./pages/member/MemberSettings";
import MemberNotifications from "./pages/member/MemberNotifications";
import SupportCenter from "./pages/member/SupportCenter";
import AdminLoanApply from "./pages/loans/AdminLoanApply";
import LoanDashboard from "./components/loans/LoanDashboard";
import MonthlyRepayments from "./pages/MonthlyRepayments";
import LoanReports from "./pages/LoanReports";
import AdminLoanDetail from "./pages/loans/AdminLoanDetail";
import RepaymentReminders from "./pages/RepaymentReminders";
import AdminSupportTickets from "./pages/admin/AdminSupportTickets";
import MemberLayout from "./components/layouts/MemberLayout";
import AdminRoute from "./components/guards/AdminRoute";
import { PermissionGuard } from "./components/guards/PermissionGuard";
import MemberRoute from "./components/guards/MemberRoute";
import DashboardRedirect from "./components/guards/DashboardRedirect";
import PaymentHistory from "./pages/member/PaymentHistory";
import PasswordChangeGuard from "./components/guards/PasswordChangeGuard";
import { 
  GroupAccessGuard, 
  MemberAccessGuard, 
  FineAccessGuard
} from "./components/ui/guards/GroupAccessGuard";
import { AttendanceAccessGuard } from "./components/ui/guards/AttendanceAccessGuard";

const router = createBrowserRouter([
  {
    element: <App />,
    children: [
      {
        element: <RootLayout />,
        children: [
          {
            path: "/",
            element: <Login />,
          },
          {
            path: "/login",
            element: <Login />,
          },
          {
            path: "/forgot-password",
            element: <ForgotPassword />,
          },
          {
            path: "/reset-password",
            element: <ResetPassword />,
          },
          {
            path: "/change-password",
            element: <ChangePassword />,
          },
          {
            path: "/register",
            element: <Navigate to="/" replace />,
          },
        ],
      },

      {
        element: <RootLayout />,
        children: [
          {
            element: <PasswordChangeGuard />,
            children: [
              {
                element: <DashboardLayout />,
                handle: { crumb: () => "Dashboard" },
                children: [
                  {
                    element: <AdminRoute />,
                    children: [
                  {
                    element: <PermissionGuard permission="dashboard.view" />,
                    children: [
                  {
                    element: <Dashboard />,
                    path: "dashboard",
                    handle: { crumb: () => "Overview" },
                  },
                    ],
                  },
              {
                element: <PermissionGuard permission="members.view" />,
                children: [
              {
                element: <MemberAccessGuard />,
                children: [
                  {
                    element: <Members />,
                    path: "members",
                    handle: { crumb: () => "Members" },
                  },
                  {
                    element: <Members />,
                    path: "members/add",
                    handle: { crumb: () => "Add Member" },
                  },
                  {
                    element: <Members />,
                    path: "members/:memberId",
                    handle: { crumb: () => "Member Details" },
                  },
                ],
              },
                ],
              },
              {
                element: <Navigate to="/members" replace />,
                path: "teachers",
              },
              {
                element: <Navigate to="/members/add" replace />,
                path: "teachers/add",
              },
              {
                element: <Districts />,
                path: "districts",
                handle: { crumb: () => "Districts" },
              },
              {
                element: <Braches />,
                path: "zones",
                handle: { crumb: () => "Zones" },
              },
              {
                element: <SavingSeasons />,
                path: "saving-seasons",
                handle: { crumb: () => "Saving Seasons" },
              },
              {
                element: <PermissionGuard permission="members.view" />,
                children: [
              {
                element: <GroupAccessGuard />,
                children: [
                  {
                    element: <Groups />,
                    path: "saving-groups",
                    handle: { crumb: () => "Saving Groups" },
                  },
                ],
              },
                ],
              },
              {
                element: <PermissionGuard permission="contributions.view" />,
                children: [
              {
                element: <Contributions />,
                path: "contributions",
                handle: { crumb: () => "Contributions" },
              },
              {
                element: <Savings />,
                path: "savings",
                handle: { crumb: () => "Savings" },
              },
              {
                element: <Contributions />,
                path: "contributions/add",
                handle: { crumb: () => "Add Contribution" },
              },
              {
                element: <DeclareMonthlyShares />,
                path: "shares/declare",
                handle: { crumb: () => "Declare Monthly Shares" },
              },
              {
                element: <DeclareMonthlyShares />,
                path: "shares/declare-multiple",
                handle: { crumb: () => "Declare Monthly Shares" },
              },
                ],
              },
              {
                element: <PermissionGuard permission="payroll.view" />,
                children: [
              {
                element: <HRPayroll />,
                path: "payroll",
                handle: { crumb: () => "HR Payroll Management" },
              },
                ],
              },
              {
                element: <PermissionGuard permission="ai.view" />,
                children: [
              {
                element: <AIAssistant />,
                path: "ai-assistant",
                handle: { crumb: () => "AI Assistant" },
              },
                ],
              },
              {
                element: <PermissionGuard permission="loans.view" />,
                children: [
              {
                element: <Loans />,
                path: "loans",
                handle: { crumb: () => "Loans" },
              },
              {
                element: <InterestDistribution />,
                path: "interest-distribution",
                handle: { crumb: () => "Interest Distribution" },
              },
              {
                element: <LoanRepayments />,
                path: "loan-repayments",
                handle: { crumb: () => "Loan Repayments" },
              },
              {
                element: <LoanDashboard />,
                path: "loans/dashboard",
                handle: { crumb: () => "Loan Dashboard" },
              },
              {
                element: <MonthlyRepayments />,
                path: "loans/monthly-repayments",
                handle: { crumb: () => "Monthly Repayments" },
              },
              {
                element: <LoanReports />,
                path: "loans/reports",
                handle: { crumb: () => "Loan Reports" },
              },
              {
                element: <RepaymentReminders />,
                path: "loans/reminders",
                handle: { crumb: () => "Repayment Reminders" },
              },
              {
                element: <ErrorBoundary><AdminLoanApply /></ErrorBoundary>,
                path: "loans/apply",
                handle: { crumb: () => "New Loan Application" },
              },
              {
                element: <LoanRepayments />,
                path: "loans/payments",
                handle: { crumb: () => "Loan Payments" },
              },
              {
                element: <Loans />,
                path: "loans/history",
                handle: { crumb: () => "Loan History" },
              },
              {
                element: <AdminLoanDetail />,
                path: "loans/:id",
                handle: { crumb: () => "Loan Details" },
              },
                ],
              },
              {
                element: <FineAccessGuard />,
                children: [
                  {
                    element: <Fines />,
                    path: "fines",
                    handle: { crumb: () => "Fines" },
                  },
                ],
              },
              {
                element: <PenaltyManagement />,
                path: "penalties",
                handle: { crumb: () => "Penalty Management" },
              },
              {
                element: <PendingMemberPayments />,
                path: "member-payments",
                handle: { crumb: () => "Pending Member Payments" },
              },
              {
                element: <PermissionGuard permission="expenses.view" />,
                children: [
              {
                element: <Expenses />,
                path: "expenses",
                handle: { crumb: () => "Expenses" },
              },
              {
                element: <Expenses />,
                path: "expenses/add",
                handle: { crumb: () => "Add Expense" },
              },
              {
                element: <ExpenseCategories />,
                path: "expenses/categories",
                handle: { crumb: () => "Expense Categories" },
              },
                ],
              },
              {
                element: <PermissionGuard permission="settings.view" />,
                children: [
              {
                element: <FinanceAccounts />,
                path: "finance/accounts",
                handle: { crumb: () => "Accounts" },
              },
              {
                element: <AccountDetails />,
                path: "finance/accounts/:id",
                handle: { crumb: () => "Account Details" },
              },
              {
                element: <JournalEntries />,
                path: "finance/journals",
                handle: { crumb: () => "Journal Entries" },
              },
              {
                element: <ChartOfAccounts />,
                path: "finance/chart-of-accounts",
                handle: { crumb: () => "Chart of Accounts" },
              },
              {
                element: <Ledger />,
                path: "finance/ledger",
                handle: { crumb: () => "Ledger" },
              },
                ],
              },
              {
                element: <PermissionGuard permission="meetings.view" />,
                children: [
              {
                element: <AttendanceAccessGuard />,
                children: [
                  {
                    element: <Attendance />,
                    path: "attendance",
                    handle: { crumb: () => "Attendance Tracking" },
                  },
                  {
                    element: <EnterpriseAttendance />,
                    path: "attendance/enterprise",
                    handle: { crumb: () => "Enterprise Attendance" },
                  },
                ],
              },
              {
                element: <Meetings />,
                path: "meetings",
                handle: { crumb: () => "All Meetings" },
              },
              {
                element: <Meetings />,
                path: "meetings/schedule",
                handle: { crumb: () => "Schedule Meeting" },
              },
              {
                element: <Meetings />,
                path: "meetings/history",
                handle: { crumb: () => "Meeting History" },
              },
                ],
              },
              {
                element: <PermissionGuard permission="reports.view" />,
                children: [
              {
                element: <Navigate to="/reports/financial" replace />,
                path: "reports",
              },
              {
                element: <Statements />,
                path: "statements",
                handle: { crumb: () => "Statements" },
              },
              {
                element: <FinancialReport />,
                path: "reports/financial",
                handle: { crumb: () => "Financial Reports" },
              },
              {
                element: <MemberReport />,
                path: "reports/members",
                handle: { crumb: () => "Member Reports" },
              },
              {
                element: <MemberStatement />,
                path: "reports/member/:memberId/statement",
                handle: { crumb: () => "Member Statement" },
              },
              {
                element: <SavingsReportPage />,
                path: "reports/savings",
                handle: { crumb: () => "Savings Reports" },
              },
              {
                element: <LoanReportPage />,
                path: "reports/loans",
                handle: { crumb: () => "Loan Reports" },
              },
              {
                element: <RepaymentReportPage />,
                path: "reports/repayments",
                handle: { crumb: () => "Repayment Reports" },
              },
              {
                element: <PayrollReportPage />,
                path: "reports/payroll",
                handle: { crumb: () => "Payroll Deduction Reports" },
              },
              {
                element: <ApprovalReportPage />,
                path: "reports/approvals",
                handle: { crumb: () => "Approval Reports" },
              },
              {
                element: <MemberContributionStatement />,
                path: "reports/contribution-statement",
                handle: { crumb: () => "Contribution Statement" },
              },
                ],
              },
              {
                element: <PermissionGuard permission="loans.approve" />,
                children: [
              {
                element: <ApprovalsCenter />,
                path: "approvals",
                handle: { crumb: () => "Approvals Center" },
              },
                ],
              },
              {
                element: <PermissionGuard permission="settings.view" />,
                children: [
              {
                element: <AuditLogs />,
                path: "audit-logs",
                handle: { crumb: () => "Audit Logs" },
              },
              {
                element: <AdminSupportTickets />,
                path: "support-tickets",
                handle: { crumb: () => "Member Questions" },
              },
                ],
              },
            ],
          },
          {
            path: "settings",
            handle: { crumb: () => "Settings" },
            children: [
              {
                element: <PermissionGuard permission="settings.manage" />,
                children: [
              {
                element: <ErrorBoundary><Settings /></ErrorBoundary>,
                index: true,
              },
              {
                element: <LoanProductForm />,
                path: "loan-types/new",
                handle: { crumb: () => "New Loan Type" },
              },
              {
                element: <LoanProductForm />,
                path: "loan-types/:id/edit",
                handle: { crumb: () => "Edit Loan Type" },
              },
              {
                element: <GroupInfo />,
                path: "group-info",
                handle: { crumb: () => "Group Information" },
              },
              {
                element: <AccountSettings />,
                path: "account",
                handle: { crumb: () => "Account Settings" },
              },
              {
                element: <ExpenseCategories />,
                path: "expense-categories",
                handle: { crumb: () => "Expense Categories" },
              },
              {
                element: <UsersRoles />,
                path: "users-roles",
                handle: { crumb: () => "Users & Roles" },
              },
              {
                element: <RolesPermissions />,
                path: "permissions",
                handle: { crumb: () => "User Permissions" },
              },
              {
                element: <ApprovalWorkflowsSettings />,
                path: "approval-workflows",
                handle: { crumb: () => "Approval Workflows" },
              },
              {
                element: <SystemUsers />,
                path: "users",
                handle: { crumb: () => "System Users" },
              },
              {
                element: <AccountSettings />,
                path: "account-settings",
                handle: { crumb: () => "Account settings" },
              },
              {
                element: <SystemUsers />,
                path: "system-users",
                handle: { crumb: () => "System Users" },
              },
              {
                element: <RolesPermissions />,
                path: "role-permissions",
                handle: { crumb: () => "Roles & permissions" },
              },
              {
                element: <GeneralSettings />,
                path: "general-information",
                handle: { crumb: () => "General settings" },
              },
                ],
              },
            ],
          },
            ],
          },
          {
            path: "access-denied",
            element: <AccessDenied />,
            handle: { crumb: () => "Access denied" },
          },
          {
            path: "help",
            element: <DashboardLayout />,
            handle: { crumb: () => "Help & Support" },
            children: [
              {
                element: <HelpGuide />,
                path: "guide",
                handle: { crumb: () => "User Guide" },
              },
              {
                element: <FAQ />,
                path: "faq",
                handle: { crumb: () => "FAQ" },
              },
              {
                element: <ContactSupport />,
                path: "support",
                handle: { crumb: () => "Contact Support" },
              },
              {
                element: <SystemStatus />,
                path: "status",
                handle: { crumb: () => "System Status" },
              },
            ],
          },
          {
            path: "member",
            element: <DashboardLayout />,
            handle: { crumb: () => "Member Portal" },
            children: [
              {
                element: <MemberRoute />,
                children: [
              {
                element: <MemberLayout />,
                children: [
                  {
                    element: <MemberDashboard />,
                    path: "dashboard",
                    handle: { crumb: () => "My Dashboard" },
                  },
                  {
                    element: <MyContributions />,
                    path: "contributions",
                    handle: { crumb: () => "My Contributions" },
                  },
                  {
                    element: <RecordSaving />,
                    path: "savings/record",
                    handle: { crumb: () => "Record Saving" },
                  },
                  {
                    element: <MyShares />,
                    path: "shares",
                    handle: { crumb: () => "My Shares" },
                  },
                  {
                    element: <Navigate to="/member/contributions" replace />,
                    path: "contributions/summary",
                  },
                  {
                    element: <MyProfile />,
                    path: "profile",
                    handle: { crumb: () => "My Profile" },
                  },
                  {
                    element: <MyLoans />,
                    path: "loans",
                    handle: { crumb: () => "Loan Dashboard" },
                  },
                  {
                    element: <ErrorBoundary><ApplyForLoan /></ErrorBoundary>,
                    path: "loans/apply",
                    handle: { crumb: () => "Apply for Loan" },
                  },
                  {
                    element: <SelectInsurers />,
                    path: "loans/insurers",
                    handle: { crumb: () => "Insurers" },
                  },
                  {
                    element: <MyGuarantorRequests />,
                    path: "loans/guarantor-requests",
                    handle: { crumb: () => "Guarantor Requests" },
                  },
                  {
                    element: <DeclareLoanPayment />,
                    path: "loans/declare-payment",
                    handle: { crumb: () => "Declare Loan Payment" },
                  },
                  {
                    element: <PaymentHistory />,
                    path: "loans/payment-history",
                    handle: { crumb: () => "Payment History" },
                  },
                  {
                    path: "loans/:id",
                    handle: { crumb: () => "Loan Details" },
                    children: [
                      {
                        index: true,
                        lazy: async () => {
                          const { default: LoanDetail } = await import("./pages/member/LoanDetail");
                          return { Component: () => <ErrorBoundary><LoanDetail /></ErrorBoundary> };
                        },
                      },
                    ],
                  },
                  {
                    element: <MemberMeetings />,
                    path: "meetings",
                    handle: { crumb: () => "Meetings" },
                  },
                  {
                    element: <ReportsLayout />,
                    path: "reports",
                    handle: { crumb: () => "Reports" },
                    children: [
                      {
                        element: <Navigate to="/member/reports/dashboard" replace />,
                        index: true,
                      },
                      {
                        element: <ReportsDashboard />,
                        path: "dashboard",
                        handle: { crumb: () => "Dashboard Report" },
                      },
                      {
                        element: <SavingsReport />,
                        path: "savings",
                        handle: { crumb: () => "Savings Report" },
                      },
                      {
                        element: <ContributionsReport />,
                        path: "contributions",
                        handle: { crumb: () => "Contributions Report" },
                      },
                      {
                        element: <LoanReport />,
                        path: "loans",
                        handle: { crumb: () => "Loan Report" },
                      },
                      {
                        element: <RepaymentsReport />,
                        path: "repayments",
                        handle: { crumb: () => "Repayments Report" },
                      },
                      {
                        element: <AccountStatement />,
                        path: "statement",
                        handle: { crumb: () => "Account Statement" },
                      },
                    ],
                  },
                  {
                    element: <MyPenalties />,
                    path: "penalties",
                    handle: { crumb: () => "My Penalties" },
                  },
                  {
                    element: <MemberNotifications />,
                    path: "notifications",
                    handle: { crumb: () => "Notifications" },
                  },
                  {
                    element: <SupportCenter />,
                    path: "support",
                    handle: { crumb: () => "Support Center" },
                  },
                  {
                    element: <MemberSettings />,
                    path: "settings",
                    handle: { crumb: () => "Settings" },
                  },
                ],
              },
                ],
              },
            ],
          },
          {
            path: "shares",
            element: <DashboardLayout />,
            handle: { crumb: () => "Shares" },
            children: [
              {
                element: <PermissionGuard permission="contributions.view" />,
                children: [
              {
                element: <DeclareMonthlyShares />,
                path: "declare",
                handle: { crumb: () => "Declare Monthly Shares" },
              },
                ],
              },
            ],
          },
          {
            path: "payroll",
            element: <DashboardLayout />,
            handle: { crumb: () => "HR" },
            children: [
              {
                element: <PermissionGuard permission="payroll.view" />,
                children: [
              {
                element: <HRPayroll />,
                index: true,
                handle: { crumb: () => "Payroll" },
              },
                ],
              },
            ],
          },
            ],
          },
        ],
      },
      {
        path: "*",
        element: <Page404 />,
        handle: { crumb: () => "Page not found" },
      },
    ],
  },
]);

export default router;
